import type { CreateGhaVoucherInput } from 'actions/MetasearchVoucherActions'
import type { DiscountRequestItem } from 'api/mappers/promoMap'
import type { GuestCheckoutOrderRequest } from 'api/guestCheckoutOrder'
import { APPLE_PAY_PAYMENT_TYPE, GOOGLE_PAY_PAYMENT_TYPE, STRIPE_PAYMENT_ELEMENT_CARD_TYPE, type PaymentMethod } from 'constants/payment'
import { SOURCE_TYPES } from 'constants/source'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { isRecord } from 'lib/object/objectUtils'
import { serialiseOccupancy } from 'lib/search/occupancy'
import { reportClientError } from 'services/errorReportingService'

/** Card, Apple Pay and Google Pay use the existing Stripe guest pay-in-full flow. */
export const GUEST_CHECKOUT_PAYMENT_METHODS: ReadonlyArray<PaymentMethod> = [
  STRIPE_PAYMENT_ELEMENT_CARD_TYPE,
  APPLE_PAY_PAYMENT_TYPE,
  GOOGLE_PAY_PAYMENT_TYPE,
]

export class UnsupportedGuestGhaVoucherOccupancyError extends Error {
  constructor() {
    super('Guest checkout GHA voucher occupancy is unsupported')
  }
}

/**
 * GHA-404: svc-order withheld the GHA discount for the guest's email; no order was created.
 * Carries the property from the request, so repricing doesn't depend on the voucher stash.
 */
export class GuestGhaDiscountWithheldError extends Error {
  constructor(readonly propertyId: string | undefined) {
    super('Guest checkout GHA discount was withheld')
  }
}

function isNonBlankString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function isGuestCheckoutOrderItem(item: unknown): item is GuestCheckoutOrderRequest['cart']['items'][number] {
  if (!isRecord(item) || !isNonBlankString(item.transaction_key)) {
    return false
  }

  if (item.type === 'bedbank') return true

  // LPC formatter output has no type; svc-order treats it as legacy accommodation.
  return (item.type === undefined || item.type === 'accommodation') &&
    item.offer_type === OFFER_TYPE_ALWAYS_ON &&
    item.offer_booking_type === 'reservation' &&
    item.reservation_type === RESERVATION_TYPE_INSTANT_BOOKING &&
    isNonBlankString(item.offer_id) &&
    isNonBlankString(item.package_id)
}

interface BuildGuestCheckoutOrderRequestInput {
  contact: App.Checkout.TravellerFormData
  currencyCode: string
  regionCode: GuestCheckoutOrderRequest['cart']['region_code']
  items: Array<unknown>
  discountItems: Array<DiscountRequestItem>
  payments: App.PaymentPlan
  transactionKey: string
  recaptchaResponse: string
  pendingGhaVoucher?: CreateGhaVoucherInput
  includeGhaVoucher?: boolean
}

export function buildGuestCheckoutOrderRequest(
  input: BuildGuestCheckoutOrderRequestInput,
): GuestCheckoutOrderRequest {
  const { contact, currencyCode, regionCode, items, discountItems, payments, transactionKey, recaptchaResponse, pendingGhaVoucher, includeGhaVoucher = true } = input
  const payment = payments.payments[0]

  if (!contact.email || !contact.firstName || !contact.lastName) {
    throw new Error('Guest checkout contact details are incomplete')
  }

  if (payments.payments.length !== 1 || payment.type !== 'stripe' || typeof payment.currency !== 'string') {
    throw new Error('Guest checkout requires one Stripe pay-in-full payment')
  }

  if (!items.every(isGuestCheckoutOrderItem) || items.length !== 1) {
    const error = new Error('Guest checkout requires one valid bedbank or LPC order item')
    reportClientError(error, {
      context: 'guest_checkout_invalid_order_items',
      itemCount: items.length,
      items: items.map(item => {
        const fields = isRecord(item) ? item : undefined
        const type = typeof fields?.type === 'string' ? fields.type : fields?.offer_type
        return {
          type: typeof type === 'string' ? type : 'unknown',
          missingTransactionKey: !isNonBlankString(fields?.transaction_key),
        }
      }),
    })
    throw error
  }

  let ghaContext: Pick<GuestCheckoutOrderRequest, 'gha' | 'gpid'> = {}
  // A tokenless landing (GHA-585) has no gpid but still names its leg through the rate rule ID.
  // svc-order forwards that to svc-connection-google, which mints against the property's current
  // config. Neither identifier means this was never a metasearch landing, so no GHA context.
  if (pendingGhaVoucher?.token || pendingGhaVoucher?.rateRuleId) {
    const {
      token,
      accountType,
      propertyId,
      checkIn,
      checkOut,
      rooms,
      rateRuleId,
      source,
      advertisedPrice,
      advertisedCurrency,
    } = pendingGhaVoucher
    if (!propertyId || !checkIn || !checkOut || !rooms?.length) {
      throw new Error('Guest checkout GHA voucher context is incomplete')
    }

    const occupancy = serialiseOccupancy(rooms)
    const occupancyValue = occupancy[0]
    if (occupancy.length !== 1 || (occupancyValue !== '1' && occupancyValue !== '2')) {
      throw new UnsupportedGuestGhaVoucherOccupancyError()
    }

    ghaContext = {
      ...(token && { gpid: token }),
      gha: {
        property_id: propertyId,
        check_in: checkIn,
        check_out: checkOut,
        occupancy: [occupancyValue],
        is_cug_rate: !!source?.includes(SOURCE_TYPES.CUG_METASEARCH),
        has_voucher: includeGhaVoucher,
        ...(accountType && { account_type: accountType }),
        ...(rateRuleId && { rate_rule_id: rateRuleId }),
        // Both or neither: a price the service cannot compare against the charged currency buys
        // no hold, so there is no point sending it.
        ...(advertisedPrice != null && advertisedCurrency && {
          advertised_price: advertisedPrice,
          advertised_currency: advertisedCurrency,
        }),
      },
    }
  }

  return {
    platform: 'web',
    contact: {
      email: contact.email,
      first_name: contact.firstName,
      last_name: contact.lastName,
      ...(contact.phone && { phone: contact.phone }),
      ...(contact.phonePrefix && { phone_prefix: contact.phonePrefix }),
    },
    cart: {
      currency_code: currencyCode,
      region_code: regionCode,
      items,
      discount_items: discountItems,
      payments: {
        currency: payments.currency,
        isDepositPayment: false,
        isDeferredPayment: false,
        isPaymentSchedulePayment: false,
        payments: [{
          amount: payment.amount,
          currency: payment.currency,
          type: 'stripe',
        }],
      },
    },
    ...ghaContext,
    transaction_key: transactionKey,
    recaptchaResponse,
  }
}
